import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Input,
  Button,
  Text,
  Link,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircle24Filled } from '@fluentui/react-icons';
import { apiFetch } from '../lib/apiFetch';
import { isSandbox } from '../config/appEnv';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding('24px', '16px'),
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('40px'),
    boxShadow: tokens.shadow16,
    boxSizing: 'border-box',
    '@media (max-width: 480px)': {
      ...shorthands.padding('28px', '20px'),
      boxShadow: tokens.shadow8,
    },
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logoImage: {
    height: '48px',
    '@media (max-width: 480px)': {
      height: '40px',
    },
  },
  sandboxBadge: {
    display: 'block',
    width: 'fit-content',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '12px',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    color: '#ffffff',
    backgroundColor: '#c2410c',
    ...shorthands.padding('2px', '8px'),
    ...shorthands.borderRadius('4px'),
  },
  title: {
    textAlign: 'center' as const,
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
    display: 'block',
    '@media (max-width: 480px)': {
      fontSize: '18px',
    },
  },
  subtitle: {
    textAlign: 'center' as const,
    display: 'block',
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '32px',
    '@media (max-width: 480px)': {
      marginBottom: '24px',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground1,
  },
  error: {
    backgroundColor: tokens.colorStatusDangerBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorStatusDangerBorder1),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('10px', '12px'),
    color: tokens.colorStatusDangerForeground1,
    fontSize: '14px',
  },
  back: {
    textAlign: 'center' as const,
    fontSize: '14px',
  },
  success: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center' as const,
    gap: '12px',
  },
  successIcon: {
    color: tokens.colorStatusSuccessForeground1,
    fontSize: '40px',
  },
  successText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
});

export default function ForgotPassword() {
  const styles = useStyles();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiFetch('/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      // The backend always returns a non-enumerating 200; treat any 2xx as sent.
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Something went wrong. Please try again.');
      }
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <img src="/logo-tradewinds.png" alt="Tradewinds Mechanical" className={styles.logoImage} />
        </div>

        {isSandbox() && <span className={styles.sandboxBadge}>SANDBOX</span>}

        {sent ? (
          <div className={styles.success}>
            <CheckmarkCircle24Filled className={styles.successIcon} />
            <Text as="h1" className={styles.title}>Check your email</Text>
            <Text className={styles.successText}>
              If an account exists for that email, we've sent a link to reset your
              password. The link expires in 1 hour.
            </Text>
            <Link onClick={() => navigate('/login')}>Back to sign in</Link>
          </div>
        ) : (
          <>
            <Text as="h1" className={styles.title}>Forgot your password?</Text>
            <Text className={styles.subtitle}>
              Enter your email and we'll send you a reset link.
            </Text>

            <form className={styles.form} onSubmit={handleSubmit}>
              {error && <div className={styles.error}>{error}</div>}

              <div className={styles.field}>
                <label className={styles.label}>Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(_, data) => setEmail(data.value)}
                  placeholder="you@example.com"
                  size="large"
                  disabled={submitting}
                />
              </div>

              <Button appearance="primary" type="submit" size="large" disabled={submitting}>
                {submitting ? <Spinner size="tiny" /> : 'Send reset link'}
              </Button>

              <div className={styles.back}>
                <Link onClick={() => navigate('/login')} disabled={submitting}>
                  Back to sign in
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
